import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import {
  SetLinkList,
  addLink,
  SetLinkEdit,
  SetLinkDel,
} from 'lib/api/newpagesetting';
import { showSuccessSnackbar } from 'modules/snackbar';

import MbButton from 'components/atoms/MbButton';
import NewInput from 'components/atoms/NewInput';
import MbTooltip from 'components/atoms/MbTooltip';
import NewButton from 'components/molecules/NewButton';
import TableContainer from 'components/molecules/MbTable';
import FunctionWrap from 'components/molecules/FunctionWrap';
import TableBtnWrap from 'components/molecules/TableBtnWrap';
import Modal from 'components/organisms/NewModal';

const Statistics = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const initModal = {
    open: false,
    type: null,
    params: {},
    modalTitle: '',
    confirmBtnTitle: '',
  };
  const [modal, setModal] = useState(initModal);
  const [reFetch, setReFetch] = useState(false);

  // header 및 body 컬럼 커스텀
  const customized = {
    MANAGE_ICONS: { headStyle: { width: 80 } },
    PAGE_DOMAINS_STR: { merged: true },
  };

  // 화면에서 추가될 컬럼
  const addColumnList = (() => {
    const result = new Map();
    result.set(0, {
      id: 'MANAGE_ICONS',
      label: t('관리'),
      align: 'center',
      makeRowFunc: (data) => {
        return (
          <TableBtnWrap>
            <MbTooltip title={t('수정')}>
              <MbButton
                iconBtn
                name="edit"
                icon="faEdit"
                onClick={(e) => handleModalOpen(e, data)}
              />
            </MbTooltip>
            <MbTooltip title={t('삭제')}>
              <MbButton
                iconBtn
                data-seq={data.SEQ}
                name="delete"
                icon="faTrashAlt"
                onClick={(e) => handleModalOpen(e, data)}
              />
            </MbTooltip>
          </TableBtnWrap>
        );
      },
    });
    return result;
  })();

  /**
   * modal 열어주는 함수
   */
  const handleModalOpen = (e, rowData) => {
    const { name } = e.currentTarget;
    let newModal = { ...initModal };
    newModal = { ...newModal, open: true, type: name };
    if (name === 'add') {
      newModal = { ...newModal, modalTitle: '등록', confirmBtnTitle: '등록' };
    } else if (name === 'edit') {
      newModal = {
        ...newModal,
        modalTitle: '수정',
        confirmBtnTitle: '수정',
        params: {
          domains: rowData['PAGE_DOMAINS_STR'],
          title: rowData['PAGE_TITLE'],
          seq: rowData['SEQ'],
        },
      };
    } else if (name === 'delete') {
      newModal = {
        ...newModal,
        modalTitle: '삭제',
        confirmBtnTitle: '삭제',
        params: {
          domains: rowData['PAGE_DOMAINS_STR'],
          title: rowData['PAGE_TITLE'],
          seq: rowData['SEQ'],
        },
      };
    }
    setModal({ ...newModal });
  };

  /**
   * 모달 닫아주는 함수
   */
  const handleModalClose = () => {
    let newModal = modal;
    newModal = { ...modal, open: false };
    setModal(newModal);
  };

  /**
   * 모달에 submit 버튼을 눌렀을 때 실행되는 함수
   */
  const handleSubmit = () => {
    let newParams = { ...modal.params };

    if (modal.type === 'add') {
      addLink(newParams)
        .then((response) => {
          const { message } = response.data;
          handleClickSnackbar({
            message: message,
            option: { type: 'success' },
          });
          setReFetch(true);
        })
        .catch((error) => {
          handleClickSnackbar({
            message: error,
            option: { type: 'error' },
          });
        })
        .finally(() => {
          setReFetch(false);
        });
    } else if (modal.type === 'edit') {
      SetLinkEdit(newParams)
        .then((response) => {
          const { message } = response.data;
          handleClickSnackbar({
            message: message,
            option: { type: 'success' },
          });
          setReFetch(true);
        })
        .catch((error) => {
          handleClickSnackbar({
            message: error,
            option: { type: 'error' },
          });
        })
        .finally(() => {
          setReFetch(false);
        });
    } else if (modal.type === 'delete') {
      SetLinkDel({ seq: newParams.seq })
        .then((response) => {
          const { message } = response.data;
          handleClickSnackbar({
            message: message,
            option: { type: 'success' },
          });
          setReFetch(true);
        })
        .catch((error) => {
          handleClickSnackbar({
            message: error,
            option: { type: 'error' },
          });
        })
        .finally(() => {
          setReFetch(false);
        });
    }
    setModal({ ...modal, open: false });
  };

  /**
   * 모달 값을 변경하는 함수
   * @param newParams
   * @param name
   */
  const modalGetParam = (newParams, name) => {
    setModal({ ...modal, params: { ...newParams } });
  };

  const handleClickSnackbar = useCallback((snackbarOptions) => {
    dispatch(showSuccessSnackbar(snackbarOptions));
    //eslint-disable-next-line
  }, []);

  return (
    <>
      <FunctionWrap loading={false}>
        <div>
          <NewButton
            type="modalBtn"
            name="add"
            onClick={handleModalOpen}
            disableRipple
          >
            {t('등록')}
          </NewButton>
        </div>
      </FunctionWrap>
      <TableContainer
        dataReqPromise={SetLinkList}
        params={{}}
        // params 값이 없이 사용하는 테이블
        reGetData={reFetch}
        sort="desc"
        order="PAGE_TITLE"
        dense={true}
        noneSearch
        addColumnList={addColumnList}
        customized={customized}
        noScroll
        useColumSet={false}
        toolTip={t('매체 첫번째 새창링크 제한할 이전주소를 추가합니다.')}
      />
      <Modal
        open={modal.open}
        headerTitle={modal.modalTitle}
        handleClose={handleModalClose}
        confirmBtnTitle={modal.confirmBtnTitle}
        confirmOnClick={handleSubmit}
        useForm={modal.type !== 'delete'}
        toolTip="팝업제한하는 도메인을 추가할 수 있습니다."
      >
        {(modal.type === 'add' || modal.type === 'edit') && (
          <>
            <tr>
              <th>{t('매체명')}</th>
              <td>
                <NewInput
                  type="tableInput"
                  name="title"
                  params={modal.params}
                  value={modal.params.title}
                  callBack={modalGetParam}
                />
              </td>
            </tr>
            <tr>
              <th>{t('도메인')}</th>
              <td>
                <NewInput
                  type="tableInput"
                  name="domains"
                  params={modal.params}
                  value={modal.params.domains}
                  callBack={modalGetParam}
                  placeholder="(,) 문자로 구분됩니다."
                />
              </td>
            </tr>
          </>
        )}
        {modal.type === 'delete' && (
          <p>
            {modal.params.title === '' ? '선택하신 항목' : modal.params.title}
            {t('을(를) 삭제하시겠습니까?')}
          </p>
        )}
      </Modal>
    </>
  );
};

export default Statistics;
